import * as React from 'react';
import { InjectedTranslateProps, translate } from 'react-i18next';
import { Search, TPAModal } from '@wix/social-groups-common';
import { compose } from '@wix/social-groups-common/dist/src/compose';
import { Grid } from 'wix-ui-tpa/Grid';
import { Text } from 'wix-ui-tpa/Text';
import { MemberCard } from '../../MemberCard/MemberCard';
import { withAppData, WithAppDataProps, withSiteMembers } from '../../Context';
import { memberWrapper } from '@wix/social-groups-api';
import { ApiTypes } from '@wix/social-groups-api/dist/src/types';
import { getMembersKey } from '../../../MembersLabel/MembersLabel';
import { WithSiteMembers } from '../../Context/withSiteMembers';
import {
  BIUserEntry,
  tryToCallBi,
  withBiLogger,
  WithBiLoggerProps,
} from '@wix/social-groups-common/dist/src/context';
import { EmptyState } from '../../EmptyState';
import { isValidEmail } from '@wix/social-groups-common/dist/src/utils';
import { LetterIcon } from '../../../icons/LetterIcon';
import {
  InviteMembersProps,
  withInviteMembers,
} from '../../Context/InviteMembersProps';

import styles from './AddMembersModal.st.css';

const INVITE_SENT_TIMEOUT = 1000;

export interface AddMembersModalProps {
  isOpen: boolean;
  group: ApiTypes.v1.GroupResponse;
  currentMember: ApiTypes.v1.GroupMemberResponse;

  handleClose();
}

type AddMembersProps = AddMembersModalProps &
  InjectedTranslateProps &
  WithSiteMembers &
  InviteMembersProps &
  WithAppDataProps &
  WithBiLoggerProps;

interface AddMembersModalState {
  searchQuery: string;
  invitesMap: { [email: string]: number };
}

class AddMembersModalComponent extends React.Component<
  AddMembersProps,
  AddMembersModalState
> {
  static defaultProps = {
    siteMembers: [],
  };

  state = {
    searchQuery: '',
    invitesMap: {},
  };

  componentDidUpdate(prevProps: Readonly<AddMembersProps>) {
    if (prevProps.isOpen && !this.props.isOpen) {
      this.setState({ searchQuery: '' });
    }
  }

  setSearchQuery = (value: string) => {
    this.setState({ searchQuery: value });
  };

  render() {
    const { t, isOpen, handleClose, group } = this.props;
    const { searchQuery } = this.state;
    const membersLabel = t(getMembersKey(group));
    const title = t('groups-web.add.members.title', { membersLabel });
    const siteMembers = this.getSiteMembers();
    const hasSiteMembers = !!siteMembers.length;

    return (
      <TPAModal
        isOpen={isOpen}
        onRequestClose={handleClose}
        {...styles('root', {}, this.props)}
      >
        <Text className={styles.title}>{title}</Text>
        <div className={styles.searchWrapper}>
          <Search
            onChange={this.setSearchQuery}
            withBorder={true}
            fullWidth={true}
            forceOpen={true}
            placeholder={t(
              hasSiteMembers
                ? 'groups-web.add.members.search.placeholder'
                : 'groups-web.add.members.search.no-site-members.placeholder',
              { count: siteMembers.length },
            )}
            withCloseButton={!!searchQuery}
          />
        </div>
        <div className={styles.content}>
          <Grid maxColumns={1} width={220} minColumnWidth={220} rowGap={0}>
            {this.renderMembers(siteMembers)}
          </Grid>
        </div>
      </TPAModal>
    );
  }

  addMember = memberId => {
    const { biLogger, group } = this.props;
    tryToCallBi(async () => {
      await biLogger.groupsAddMemberClicked({
        origin: 'modal_plus_add_btn',
        groupId: group.groupId,
        userEntry: BIUserEntry.SITE,
      });
    });
    return this.props.addMember(this.props.group, memberId);
  };

  inviteMemberByEmail = () => {
    const { inviteMemberByEmail, biLogger } = this.props;
    const { searchQuery } = this.state;
    tryToCallBi(async () => {
      await biLogger.inviteSent({
        group_id: this.props.group.groupId,
        site_member_id: null,
        contact: searchQuery,
        origin: 'dialog_scr_invite_btn',
      } as any);
    });
    this.addEmailToInvitesSent(searchQuery);
    inviteMemberByEmail(searchQuery);
  };

  private addEmailToInvitesSent(email: string) {
    const { invitesMap } = this.state;
    const timeout = () => {
      return setTimeout(() => {
        this.removeEmailFromInvitesSent(email);
      }, INVITE_SENT_TIMEOUT);
    };
    this.setState({ invitesMap: { ...invitesMap, [email]: timeout() } });
  }

  private removeEmailFromInvitesSent(email: string) {
    const { invitesMap } = this.state;
    this.setState({ invitesMap: { ...invitesMap, [email]: null } });
  }

  private renderMembers(siteMembers: ApiTypes.v1.GroupMemberResponse[]) {
    if (!siteMembers.length) {
      return [
        <Grid.Item colSpan={1} rowSpan={1} key={'no-members'}>
          {isValidEmail(this.state.searchQuery)
            ? this.renderInvites()
            : this.renderNoSiteMembersState()}
        </Grid.Item>,
      ];
    }
    return siteMembers.map((member, i) => {
      const { t } = this.props;
      const { name, imageUrl, relationship } = memberWrapper(member);
      return (
        <Grid.Item colSpan={1} rowSpan={1} key={member.siteMemberId}>
          <MemberCard
            name={name.nick || t('groups-web.member.anonymous')}
            image={imageUrl}
            done={relationship !== ApiTypes.v1.RelationshipWithGroup.NONE}
            onActionClick={() => this.addMember(member.siteMemberId)}
            actionLabel={t('groups-web.add')}
            actionDoneLabel={t('groups-web.added')}
            withDivider={i !== siteMembers.length - 1}
          />
        </Grid.Item>
      );
    });
  }

  private renderInvites() {
    const { t } = this.props;
    const { invitesMap, searchQuery } = this.state;
    return (
      <MemberCard
        name={this.state.searchQuery}
        image={<LetterIcon className={styles.inviteIcon} />}
        done={!!invitesMap[searchQuery]}
        onActionClick={this.inviteMemberByEmail}
        actionLabel={t('groups-web.invite')}
        actionDoneLabel={t('groups-web.invited')}
      />
    );
  }

  private renderNoSiteMembersState() {
    const { t } = this.props;
    const { searchQuery } = this.state;
    return (
      <EmptyState
        title={searchQuery ? null : t('group-web.members.empty')}
        content={t(
          searchQuery
            ? 'group-web.members.empty-search-match.caption'
            : 'group-web.members.empty.caption',
        )}
      />
    );
  }

  private getSiteMembers() {
    const { siteMembers, currentMember } = this.props;
    const { searchQuery } = this.state;
    const { siteMemberId } = currentMember;
    return siteMembers.filter(
      m =>
        m.siteMemberId !== siteMemberId &&
        m.name &&
        m.name.nick &&
        m.name.nick
          .toLocaleLowerCase()
          .includes(searchQuery.toLocaleLowerCase()),
    );
  }
}

const enhance = compose(
  translate(),
  withSiteMembers,
  withBiLogger,
  withAppData,
  withInviteMembers,
);

export const AddMembersModal = enhance(
  AddMembersModalComponent,
) as React.ComponentType<AddMembersModalProps>;
